/**
 * Device Detection Utility
 *
 * 
 * Provides functions for detecting device type, orientation, and matching
 * viewport dimensions to device configurations.
 *
 * @module deviceDetection
 */

import {
  DEVICE_VIEWPORTS,
  getDeviceConfig,
  findMatchingBreakpoint,
  getPageFlipMode
} from '../config/deviceBreakpoints.js';

/**
 * Detect current device and orientation based on viewport dimensions
 * @returns {Object} { deviceId, orientation, mode, viewport }
 */
export function detectDeviceAndOrientation() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const orientation = width > height ? 'landscape' : 'portrait';

  // Try to match exact viewport dimensions to a known device
  for (const [deviceId, config] of Object.entries(DEVICE_VIEWPORTS)) {
    const vp = config[orientation];
    if (vp && vp.width === width && vp.height === height) {
      const mode = getPageFlipMode(deviceId, orientation);
      return {
        deviceId,
        orientation,
        mode: mode || 'unknown',
        viewport: { width, height }
      };
    }
  }

  // Fallback: determine generic device type by viewport size
  if (width < 768) {
    return {
      deviceId: 'generic-phone',
      orientation,
      mode: orientation === 'landscape' ? 'single-rotated' : 'single',
      viewport: { width, height }
    };
  } else if (width >= 768 && width < 1024) {
    return {
      deviceId: 'generic-tablet',
      orientation,
      mode: orientation === 'landscape' ? 'double' : 'single',
      viewport: { width, height }
    };
  } else {
    // Desktop or smart display (Echo Show etc.): width >= 1024px
    return {
      deviceId: 'generic-desktop',
      orientation,
      mode: 'double', // Desktop/smart display always uses double page mode
      viewport: { width, height }
    };
  }
}

/**
 * Get current orientation
 * @returns {string} 'portrait' or 'landscape'
 */
export function getOrientation() {
  return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
}

/**
 * Check if viewport matches a specific device
 * @param {string} deviceId - Device identifier to check
 * @param {string} orientation - Orientation to check
 * @returns {boolean} True if current viewport matches device
 */
export function matchesDevice(deviceId, orientation = null) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const currentOrientation = orientation || getOrientation();

  const config = getDeviceConfig(deviceId);
  if (!config) return false;

  const vp = config[currentOrientation];
  if (!vp) return false;

  return vp.width === width && vp.height === height;
}

/**
 * Get active breakpoint for current viewport
 * @returns {string|null} Breakpoint ID or null if no match
 */
export function getActiveBreakpoint() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const orientation = getOrientation();

  return findMatchingBreakpoint(width, height, orientation);
}

/**
 * Check if current viewport is mobile-sized
 * @returns {boolean} True if viewport width < 768px
 */
export function isMobile() {
  return window.innerWidth < 768;
}

/**
 * Check if current viewport is tablet-sized
 * @returns {boolean} True if viewport width between 768px and 1023px
 */
export function isTablet() {
  const width = window.innerWidth;
  return width >= 768 && width < 1024;
}

/**
 * Check if current viewport is desktop-sized
 * @returns {boolean} True if viewport width >= 1024px
 */
export function isDesktop() {
  return window.innerWidth >= 1024;
}

/**
 * Get device category
 * @returns {string} 'phone', 'tablet', 'desktop', or 'unknown'
 */
export function getDeviceCategory() {
  const { deviceId } = detectDeviceAndOrientation();

  // Check device ID first - this takes precedence over viewport size
  if (deviceId.includes('iphone') || deviceId.includes('pixel') ||
      deviceId.includes('galaxy') || deviceId === 'generic-phone') {
    return 'phone';
  }

  if (deviceId.includes('ipad') || deviceId.includes('surface') ||
      deviceId === 'generic-tablet') {
    return 'tablet';
  }

  if (deviceId.includes('nest')) {
    return 'smart-display';
  }

  // Amazon Echo Show / Silk on fixed-screen devices
  const ua = navigator.userAgent || '';
  if (/Silk/i.test(ua) && /Echo Show|AEOKN|AEOBC|AEOBP/i.test(ua)) {
    return 'smart-display';
  }

  if (deviceId.includes('fold')) {
    return 'foldable';
  }

  if (deviceId === 'generic-desktop' || isDesktop()) {
    return 'desktop';
  }

  return 'unknown';
}

/**
 * Listen for orientation changes
 * @param {Function} callback - Function to call when orientation changes
 * @returns {Function} Cleanup function to remove listener
 */
export function onOrientationChange(callback) {
  const handler = () => {
    const detection = detectDeviceAndOrientation();
    callback(detection);
  };

  // Listen to both orientationchange and resize events
  window.addEventListener('orientationchange', handler);
  window.addEventListener('resize', handler);

  // Return cleanup function
  return () => {
    window.removeEventListener('orientationchange', handler);
    window.removeEventListener('resize', handler);
  };
}

/**
 * Get viewport dimensions
 * @returns {Object} { width, height }
 */
export function getViewportDimensions() {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
}

/**
 * Check if browser supports container queries
 * @returns {boolean} True if container queries are supported
 */
export function supportsContainerQueries() {
  return CSS.supports('font-size', '1cqi');
}

/**
 * Log device detection information to console
 * Useful for debugging responsive design issues
 */
export function logDeviceInfo() {
  const detection = detectDeviceAndOrientation();
  const breakpoint = getActiveBreakpoint();
  const category = getDeviceCategory();
  const cqSupport = supportsContainerQueries();

  console.group('📱 Device Detection Info');
  console.log('Device ID:', detection.deviceId);
  console.log('Orientation:', detection.orientation);
  console.log('StPageFlip Mode:', detection.mode);
  console.log('Viewport:', `${detection.viewport.width}×${detection.viewport.height}`);
  console.log('Active Breakpoint:', breakpoint || 'none');
  console.log('Category:', category);
  console.log('Container Queries:', cqSupport ? 'supported' : 'not supported');
  console.groupEnd();

  return detection;
}

/**
 * Set device class on document root for CSS targeting
 * This enables CSS to use data attributes instead of media queries
 * 
 * Sets:
 * - data-device: Device ID or breakpoint ID
 * - data-orientation: 'portrait' or 'landscape'
 * - data-category: 'phone', 'tablet', 'desktop', etc.
 * 
 * @example
 * // CSS can now target specific devices:
 * [data-device="iphone-se"][data-orientation="portrait"] .card {
 *   font-size: 1.05rem;
 * }
 */
export function setDeviceClass() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const orientation = width > height ? 'landscape' : 'portrait';

  // Get device detection
  const detection = detectDeviceAndOrientation();
  const breakpoint = findMatchingBreakpoint(width, height, orientation);
  const category = getDeviceCategory();

  // Set data attributes on root element
  document.documentElement.dataset.device = detection.deviceId;
  document.documentElement.dataset.breakpoint = breakpoint || 'unknown';
  document.documentElement.dataset.orientation = orientation;
  document.documentElement.dataset.category = category;

  console.log('🎯 Device class set:', {
    device: detection.deviceId,
    breakpoint: breakpoint || 'unknown',
    orientation,
    category
  });
}

/**
 * Initialize device detection and set CSS classes
 * Call this on page load and listen for resize/orientation changes
 */
export function initializeDeviceDetection() {
  // Set initial device class
  setDeviceClass();

  // Update on resize (debounced)
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      setDeviceClass();
    }, 100);
  });

  // Update on orientation change
  window.addEventListener('orientationchange', () => {
    setTimeout(() => {
      setDeviceClass();
    }, 100);
  });

  console.log('✅ Device detection initialized');
}

// Auto-initialize when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDeviceDetection);
  } else {
    // DOM already loaded
    initializeDeviceDetection();
  }
}
